/**
 * Validation des saisies avec les schémas Zod partagés (les MÊMES règles que l'API), sans dépendre d'une langue :
 * chaque problème est décrit par un code stable (et ses bornes) que le front traduit (`formulaires.erreurs.<code>`,
 * 6 langues). Utilisé par le socle des formulaires du front (`useFormulaire`, React Hook Form).
 *
 * Les messages écrits dans les schémas (« Obligatoire », « Code pays ISO à 2 lettres »…) restent ceux des réponses
 * d'API ; ils ne sont pas affichés tels quels dans l'interface, sauf une clé `formulaires.erreurs.<x>` donnée en
 * message d'un contrôle personnalisé (`ctx.addIssue({ code: 'custom', message: 'formulaires.erreurs.x' })`).
 */
import type { z } from 'zod';

/** Schéma de saisie : un schéma Zod de `packages/shared` (création ou modification d'un objet). */
export type SchemaSaisie = z.ZodType;
/** Données validées et nettoyées par le schéma (espaces retirés, vide → null, majuscules…) : le corps envoyé à l'API. */
export type SortieSaisie<S extends SchemaSaisie> = z.output<S>;

export const CODES_PROBLEME_SAISIE = [
  'obligatoire', 'tropCourt', 'tropLong', 'choixObligatoire', 'choixMinimum', 'tropDeChoix', 'nombreMin', 'nombreMax',
  'email', 'url', 'format', 'valeur', 'invalide', 'personnalise',
] as const;
export type CodeProblemeSaisie = (typeof CODES_PROBLEME_SAISIE)[number];

export interface ProblemeSaisie {
  /** Champ concerné, en notation pointée (`adresse.ville`) ; indices de liste retirés (`groupesLecture`) ; `_` = formulaire. */
  champ: string;
  code: CodeProblemeSaisie;
  min?: number;
  max?: number;
  /** Contrôle personnalisé : clé `formulaires.erreurs.<x>` du dictionnaire. */
  cle?: string;
}

export const PREFIXE_MESSAGE_SAISIE = 'formulaires.erreurs.';

/** Chemin d'un problème → nom du champ du formulaire (s'arrête au premier indice de liste). */
function champDe(chemin: readonly PropertyKey[]): string {
  const segments: string[] = [];
  for (const s of chemin) {
    if (typeof s === 'number') break;
    segments.push(String(s));
  }
  return segments.join('.') || '_';
}

const nombre = (v: unknown): number | undefined => (typeof v === 'number' ? v : typeof v === 'bigint' ? Number(v) : undefined);

/** Problème Zod → problème de saisie (code stable). */
export function problemeDe(issue: z.core.$ZodIssue): ProblemeSaisie {
  const champ = champDe(issue.path);
  switch (issue.code) {
    case 'invalid_type': {
      const vide = issue.input === undefined || issue.input === null || issue.input === '';
      return { champ, code: vide ? 'obligatoire' : 'invalide' };
    }
    case 'too_small': {
      const min = nombre(issue.minimum);
      if (issue.origin === 'string') return min !== undefined && min <= 1 ? { champ, code: 'obligatoire' } : { champ, code: 'tropCourt', min };
      if (issue.origin === 'array' || issue.origin === 'set') return min !== undefined && min <= 1 ? { champ, code: 'choixObligatoire' } : { champ, code: 'choixMinimum', min };
      return { champ, code: 'nombreMin', min };
    }
    case 'too_big': {
      const max = nombre(issue.maximum);
      if (issue.origin === 'string') return { champ, code: 'tropLong', max };
      if (issue.origin === 'array' || issue.origin === 'set') return { champ, code: 'tropDeChoix', max };
      return { champ, code: 'nombreMax', max };
    }
    case 'invalid_format':
      return { champ, code: issue.format === 'email' ? 'email' : issue.format === 'url' ? 'url' : 'format' };
    case 'invalid_value':
    case 'invalid_union':
    case 'invalid_key':
      return { champ, code: 'valeur' };
    case 'custom':
      return issue.message?.startsWith(PREFIXE_MESSAGE_SAISIE)
        ? { champ, code: 'personnalise', cle: issue.message }
        : { champ, code: 'invalide' };
    default:
      return { champ, code: 'invalide' };
  }
}

/**
 * Valide `valeurs` avec `schema` : données nettoyées (à envoyer telles quelles à l'API) ou liste des problèmes, au
 * plus un par champ (le premier, dans l'ordre du schéma).
 */
export function analyserSaisie<S extends SchemaSaisie>(schema: S, valeurs: unknown):
  { ok: true; donnees: SortieSaisie<S> } | { ok: false; problemes: ProblemeSaisie[] } {
  const r = schema.safeParse(valeurs, { reportInput: true });
  if (r.success) return { ok: true, donnees: r.data };
  const vus = new Set<string>();
  const problemes: ProblemeSaisie[] = [];
  for (const issue of r.error.issues) {
    const p = problemeDe(issue);
    if (vus.has(p.champ)) continue;
    vus.add(p.champ);
    problemes.push(p);
  }
  return { ok: false, problemes };
}
